import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { getDeviceFingerprint } from '@/shared/utils/device-fingerprint'
import {
  clearPhiTransportKey,
  isPhiTransportReady,
  loadPhiTransportKey,
  unsealPhiDeep,
} from '@/shared/utils/phi-transport'

/** Auth errors that mean re-login is required — do not attempt silent refresh. */
const FORCE_RELOGIN_CODES = new Set([
  'SESSION_REVOKED',
  'SESSION_DEVICE_MISMATCH',
  'INVALID_REFRESH_TOKEN',
])

function getErrorCode(error: AxiosError): string | null {
  const data = error.response?.data as Record<string, unknown> | undefined
  if (!data?.error || typeof data.error !== 'object') return null
  const code = (data.error as Record<string, unknown>).code
  return typeof code === 'string' ? code : null
}

function forceReLogin(_errorCode?: string) {
  refreshPromise = null
  setAccessToken(null)
  if (!window.location.pathname.startsWith('/auth/')) {
    window.location.href = '/auth/login'
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1/app'

/**
 * Axios instance configured per F0.5 spec:
 * - Base URL from env
 * - JWT injection from in-memory token
 * - Automatic silent refresh on 401
 * - Proactive refresh before expiry
 * - X-Correlation-Id on every request
 * - Standard error extraction
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send refresh_token cookie
})

// ── In-memory token storage (never localStorage for JWT) ──
let accessToken: string | null = null
let refreshTimerId: ReturnType<typeof setTimeout> | undefined

// Monotonic "identity epoch". Bumped whenever the session is torn down via
// setAccessToken(null): logout, session revoke, forced re-login, and — the case
// this guards — an impersonation identity switch. Async operations that begin
// under one identity (a token refresh or a PHI-key load) capture the epoch when
// they start and DISCARD their result if it changed by the time they finish, so
// a stale Super-Admin refresh/key cannot clobber a freshly-installed impersonated
// identity. See shared/api/__tests__/impersonation-race.test.ts.
let authEpoch = 0
export function getAuthEpoch(): number {
  return authEpoch
}

export function setAccessToken(token: string | null) {
  accessToken = token
  clearTimeout(refreshTimerId)
  // The PHI transport key is bound to the session, so it must die with the token.
  // Logout, session revoke, forced re-login and identity switch all funnel here.
  if (token === null) {
    // Session boundary — invalidate any in-flight refresh / PHI-key load that
    // started under the previous identity. Nulling refreshPromise abandons the
    // shared in-flight refresh dedup so a request made under the NEW identity
    // cannot reuse (and install) the previous identity's refreshed token.
    authEpoch++
    refreshPromise = null
    clearPhiTransportKey()
  }
}

export function getAccessToken(): string | null {
  return accessToken
}

// ── Idle activity tracking (gates proactive refresh) ────────────────────────
// Real user activity = mousemove / keydown / click / scroll / touch / tab visible.
// If no activity for IDLE_LIMIT_MS, scheduled refresh skips (lets token expire).
// Server-side SESSION_TIMEOUT_MINUTES then rejects the eventual next refresh
// attempt with SESSION_INACTIVE, which the 401 interceptor turns into a logout.
//
// Cross-tab: BroadcastChannel('user-activity') keeps tabs in sync so input
// in tab A also counts for tab B. Falls back to no-op if BroadcastChannel
// is unavailable (very old browsers).
const IDLE_LIMIT_MIN = Number(import.meta.env.VITE_IDLE_LIMIT_MIN ?? 60)
const IDLE_LIMIT_MS = IDLE_LIMIT_MIN * 60 * 1000

// Server-driven idle cutoff. The BE (SESSION_TIMEOUT_MINUTES) is the source of
// truth for when a session dies; it sends the value on auth responses and we
// mirror it here so the FE idle window can't drift from the server. Until the
// first auth response supplies it, we fall back to the Vite default above.
let sessionTimeoutMs = IDLE_LIMIT_MS
export function setSessionTimeoutMinutes(min?: number | null) {
  if (typeof min === 'number' && min > 0) sessionTimeoutMs = min * 60 * 1000
}
/** Current idle cutoff in ms. Exposed for tests/debugging. */
export function getSessionTimeoutMs(): number {
  return sessionTimeoutMs
}

let lastActivityAt = Date.now()
let activityChannel: BroadcastChannel | null = null
let activityTrackerInstalled = false

function bumpActivity(localOnly = false) {
  lastActivityAt = Date.now()
  if (!localOnly && activityChannel) {
    try { activityChannel.postMessage(lastActivityAt) } catch { /* channel closed */ }
  }
}

function installActivityTracker() {
  if (activityTrackerInstalled || typeof window === 'undefined') return
  activityTrackerInstalled = true

  const onInput = () => bumpActivity()
  const onVisibility = () => { if (!document.hidden) bumpActivity() }

  // Passive listeners — never block scroll. Single handler per event type.
  window.addEventListener('mousemove', onInput, { passive: true })
  window.addEventListener('keydown', onInput, { passive: true })
  window.addEventListener('click', onInput, { passive: true })
  window.addEventListener('scroll', onInput, { passive: true, capture: true })
  window.addEventListener('touchstart', onInput, { passive: true })
  document.addEventListener('visibilitychange', onVisibility)

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      activityChannel = new BroadcastChannel('user-activity')
      activityChannel.onmessage = (ev) => {
        const ts = typeof ev.data === 'number' ? ev.data : 0
        if (ts > lastActivityAt) lastActivityAt = ts
      }
    } catch { /* channel construction failed — single-tab mode */ }
  }
}

// Auto-install on module load so any consumer of scheduleTokenRefresh benefits.
installActivityTracker()

/** Last user-activity timestamp (ms since epoch). Exposed for tests/debugging. */
export function getLastActivityAt(): number {
  return lastActivityAt
}

/**
 * Schedule a proactive token refresh ~2 min before expiry — but ONLY if the
 * user has interacted within the idle window. If idle for >IDLE_LIMIT_MS,
 * skip the refresh; the token expires naturally and the next API call (if
 * any) triggers the 401 → SESSION_INACTIVE → logout path.
 *
 * We always re-schedule (even when skipping refresh) so a returning user's
 * first interaction restores the refresh cadence on the next cycle.
 */
export function scheduleTokenRefresh(expiresInSeconds = 900) {
  clearTimeout(refreshTimerId)
  // Refresh 2 min before expiry, minimum 10s from now
  const refreshInMs = Math.max((expiresInSeconds - 120) * 1000, 10_000)
  refreshTimerId = setTimeout(async () => {
    const idleMs = Date.now() - lastActivityAt
    if (idleMs > sessionTimeoutMs) {
      // User idle past threshold — let server-side SESSION_TIMEOUT_MINUTES
      // close the session. Re-arm a short check so an interaction <expiry
      // can resume refreshing before the token actually expires.
      scheduleTokenRefresh(expiresInSeconds)
      return
    }
    const epochAtStart = authEpoch
    try {
      const { access_token, expires_in } = await silentRefresh()
      // Identity switched mid-refresh (e.g. impersonation): this token is for the
      // previous identity — discard it rather than clobber the new one.
      if (authEpoch !== epochAtStart) return
      setAccessToken(access_token)
      scheduleTokenRefresh(expires_in)
    } catch {
      // Refresh failed — will be caught by 401 interceptor on next request
    }
  }, refreshInMs)
}

// ── Request interceptor: JWT + Correlation ID + Activity hint ──
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
    // Stamp the identity this request is issued under, so the 401 handler can
    // refuse to refresh + retry it under a different identity (see below).
    ;(config as RetryableRequestConfig).__authEpoch = authEpoch
  }
  // Generate a unique correlation ID per request for tracing
  config.headers['X-Correlation-Id'] = crypto.randomUUID?.() ?? Date.now().toString(36)
  // Tell the server how long since the user last interacted. Polling on an
  // idle tab will report a large value so the server knows not to extend
  // the idle clock — preserves "logout after 1h true inactivity" semantics
  // even with React Query auto-refetch hitting every minute.
  config.headers['X-User-Activity-Ms'] = String(Date.now() - lastActivityAt)

  // A FormData body must NOT keep this instance's JSON default.
  //
  // The instance sets `Content-Type: application/json` for every request,
  // which is right for the ninety-odd JSON calls and wrong for an upload: the
  // browser only attaches a multipart boundary when it gets to set the header
  // itself. Declared as JSON, the parts never arrive — the server sees no file
  // and says the file is missing, which reads as "no PDF" on screen. Every
  // upload site had to remember to override it, and the contract upload did
  // not. Deleting it here means none of them has to.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  return config
})

// ── Response interceptor: silent refresh on 401 ──
let refreshPromise: Promise<string> | null = null

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  /**
   * The auth epoch when this request was ISSUED (stamped in the request
   * interceptor). If it no longer matches the current epoch, the identity
   * changed since — e.g. impersonation started/ended while the request was in
   * flight — so the request must not be silently refreshed and retried under the
   * new identity.
   */
  __authEpoch?: number
}

api.interceptors.response.use(
  // ── PHI transport unsealing ───────────────────────────────────────────────
  //
  // Every sealed value is replaced with its plaintext HERE, before any hook,
  // store or component sees the body. That is what keeps the rest of the app
  // unaware that sealing exists — no awaits in render paths, and no chance of a
  // component forgetting to decrypt and showing a user `MCT1.q7Fh…`.
  //
  // The key is fetched lazily on the first response rather than at login, so a
  // page refresh (which drops the in-memory key) recovers on its own without
  // needing a re-login or a bootstrap ordering rule.
  async (response) => {
    if (!isPhiTransportReady() && accessToken) {
      const epochAtStart = authEpoch
      await loadPhiTransportKey(accessToken, API_BASE_URL)
      // Identity switched during the key fetch (e.g. impersonation): the loaded
      // key is for the previous session. Drop it so the next response reloads the
      // correct one, rather than unsealing the new identity's PHI with a stale key.
      if (authEpoch !== epochAtStart) clearPhiTransportKey()
    }
    response.data = await unsealPhiDeep(response.data)
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config
    if (!originalRequest) return Promise.reject(error)
    const req = originalRequest as RetryableRequestConfig

    // If 401 and not already a refresh request
    if (error.response?.status === 401 && !originalRequest.url?.includes('/auth/refresh')) {
      // The request was issued under a specific identity (stamped in the request
      // interceptor). If the identity changed since it was issued — e.g.
      // impersonation started or ended while it was in flight — this request
      // belongs to the PREVIOUS identity. Do NOT silently refresh and retry it
      // under the new one, which would execute an admin-issued request with the
      // impersonated user's authorization (or vice versa). Fail it instead.
      if (req.__authEpoch !== undefined && req.__authEpoch !== authEpoch) {
        return Promise.reject(error)
      }

      // Don't retry if this is a business-logic 401 (e.g. wrong password),
      // only retry if it looks like token expiry. Business errors have an
      // error body with a code; token expiry typically has no body or a
      // different shape. Also prevent infinite retry via _retry flag.
      const errorCode = getErrorCode(error)

      // Session ended elsewhere, device mismatch, or revoked refresh — force re-login
      if (errorCode && FORCE_RELOGIN_CODES.has(errorCode)) {
        forceReLogin(errorCode)
        return Promise.reject(error)
      }

      const isBusinessError = errorCode && errorCode !== 'TOKEN_EXPIRED'
      const alreadyRetried = req._retry

      if (isBusinessError || alreadyRetried) {
        return Promise.reject(error)
      }

      req._retry = true

      const epochAtStart = authEpoch
      try {
        // Deduplicate concurrent refresh calls
        if (!refreshPromise) {
          const createdAtEpoch = authEpoch
          refreshPromise = silentRefresh().then(({ access_token, expires_in }) => {
            // Only re-arm the proactive refresh if the identity is unchanged since
            // this refresh started — a stale refresh must not put the new session
            // on the previous session's schedule.
            if (authEpoch === createdAtEpoch) scheduleTokenRefresh(expires_in)
            return access_token
          })
        }
        const newToken = await refreshPromise
        // Identity switched while the refresh was in flight (e.g. impersonation):
        // the refreshed token and this retry belong to the PREVIOUS identity. Do
        // not install the token, do not replay, and do NOT clear refreshPromise —
        // it may now hold a valid replacement refresh started under the new
        // identity (clearing it would let a later 401 launch a competing refresh).
        if (authEpoch !== epochAtStart) {
          return Promise.reject(error)
        }
        refreshPromise = null
        setAccessToken(newToken)

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch (refreshErr) {
        // Identity switched while the refresh was in flight (e.g. impersonation):
        // this failure belongs to the PREVIOUS identity — must NOT tear down the
        // new session (clear the token or redirect), and must NOT clear
        // refreshPromise, which may hold a valid replacement refresh started under
        // the new identity (clearing it would let a later 401 launch a competing
        // refresh and log the new session out on failure).
        if (authEpoch !== epochAtStart) {
          return Promise.reject(error)
        }
        refreshPromise = null
        const refreshCode = axios.isAxiosError(refreshErr) ? getErrorCode(refreshErr) : null
        if (refreshCode && FORCE_RELOGIN_CODES.has(refreshCode)) {
          forceReLogin(refreshCode)
        } else {
          setAccessToken(null)
          if (!window.location.pathname.startsWith('/auth/')) {
            window.location.href = '/auth/session-expired'
          }
        }
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  },
)

export interface SilentRefreshResult {
  access_token: string
  expires_in: number
  session_timeout_minutes?: number
}

export async function silentRefresh(): Promise<SilentRefreshResult> {
  const epochAtStart = authEpoch
  const fp = await getDeviceFingerprint()
  const res = await axios.post<{ access_token: string; expires_in?: number; session_timeout_minutes?: number }>(
    `${API_BASE_URL}/auth/refresh`,
    null,
    {
      withCredentials: true,
      headers: { 'X-Device-Fingerprint': fp },
    },
  )
  // Keep the FE idle window in lockstep with the server's session timeout — but
  // ONLY if the identity is unchanged since this refresh started. A stale refresh
  // (from before an impersonation / client switch) must not rewrite the new
  // session's idle window onto the previous session's schedule.
  if (authEpoch === epochAtStart) {
    setSessionTimeoutMinutes(res.data.session_timeout_minutes)
  }
  return {
    access_token: res.data.access_token,
    expires_in: res.data.expires_in ?? 900,
    session_timeout_minutes: res.data.session_timeout_minutes,
  }
}

// ── Standard error extraction ──
export interface ApiError {
  code: string
  message: string
  /**
   * Structured error context. The backend contract is an ARRAY (AppError.details
   * is `unknown[]`, serialized as `details: err.details || []`) — e.g.
   * `[{ locked_until }]` or `[{ attempts_remaining, lockout_minutes }]`. Callers
   * narrow the element shape they expect and read `[0]`.
   */
  details?: unknown[]
  status: number
}

export function extractError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (data?.error) {
      return {
        code: data.error.code ?? 'UNKNOWN',
        message: data.error.message ?? error.message,
        details: data.error.details,
        status: error.response?.status ?? 500,
      }
    }
    return {
      code: 'NETWORK_ERROR',
      message: error.message,
      status: error.response?.status ?? 0,
    }
  }
  return {
    code: 'UNKNOWN',
    message: error instanceof Error ? error.message : 'Unknown error',
    status: 0,
  }
}

export default api
